import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Block mini-capture — a scaled-down render of a block's contents
 * used to preview blocks inside container surfaces (stack peeks,
 * collection peeks).
 */
import React, { memo, useEffect, useMemo } from "react";
import { getLinkTitle, getLinkDescription, getLinkSiteName, } from "@shared/blocks/link/helpers";
import { getBlockClasses, getBlockStyleVars, persistPostitColor, removeLegacyStickyStyle } from "@/config/block-styles";
import { resolveContentComponent } from "@/components/block/block-view";
import "@/components/block/block-view.scss";
import "./block-mini-capture.scss";
function isEmptyBlock(block) {
    const c = block.content;
    if (!c || (typeof c === "object" && Object.keys(c).length === 0))
        return true;
    switch (block.type) {
        case "text":
            return !c.text && !block.title;
        case "image":
            return !c.file;
        case "audio":
            return !c.file;
        case "video":
            return !c.file;
        case "link": {
            const link = c;
            return (!link.url &&
                !getLinkTitle(link) &&
                !getLinkSiteName(link) &&
                !getLinkDescription(link) &&
                !block.title &&
                !block.summary);
        }
        case "document":
            return !c.file;
        default:
            return false;
    }
}
export const BlockMiniCapture = memo(({ envelope }) => {
    const block = envelope.block;
    const Content = resolveContentComponent(block);
    const empty = useMemo(() => isEmptyBlock(block), [block]);
    const noPadding = !empty && (block.type === "image" || block.type === "video");
    const blockClasses = useMemo(() => getBlockClasses(block), [block]);
    const blockStyle = useMemo(() => getBlockStyleVars(block), [block]);
    const isShortText = block.type === "text" && (block.content.text?.length ?? 0) < 150;
    useEffect(() => {
        if (isShortText) {
            removeLegacyStickyStyle(block.id);
            persistPostitColor();
        }
    }, [block.id, isShortText]);
    const shellClass = useMemo(() => [
        "block-view",
        noPadding && "block-view--no-padding",
        (empty || !Content) && "block-view--error",
        ...blockClasses,
    ]
        .filter(Boolean)
        .join(" "), [Content, blockClasses, empty, noPadding]);
    // Two layers only: `.block-mini-capture` plays the role of the
    // clipping viewport (sized + overflow-controlled by per-surface
    // overrides in stack/collection scss), and `.__scale` is the
    // natural-size box transformed down via `scale()` so the inner
    // block lays out at its real dimensions. Wrapping the block-view in
    // an extra "card-shell" dimensions div added nothing — block-view
    // already takes up the scale wrapper's size — so it's gone.
    return (_jsx("div", { className: "block-mini-capture", children: _jsx("div", { className: "block-mini-capture__scale", children: _jsx("div", { className: shellClass, style: blockStyle, children: !Content || empty ? (_jsx("div", { className: "card-content", children: _jsx("span", { className: "block-mini-capture__fallback-label", children: block.title ?? block.type }) })) : (_jsx(Content, { block: block })) }) }) }));
});
